import { TUTORIAL_DONE_KEY } from '~/composables/useTutorial'
import { readFlag, writeFlag } from '~/utils/flagStore'
import { PROGRESS_EPOCH } from '../../shared/progressEpoch'
import {
  cloudSigns, earlierPlay, FIRST_STEPS_CLOUD_STATE, readFirstSteps, readLocalPlay, titleCalm, titleDoors, welcomeEligible, writeFirstSteps,
  type CloudPlay, type FirstSteps, type LocalPlay,
} from '~/utils/firstSteps'

export const ONBOARDING_KEY = 'elemental-dispatch-onboarding-v1'
/** The onboarding preference values (utils/firstSteps.ts reads them too). */
export type OnboardingPreference = 'new' | 'learning' | 'dismissed' | 'complete'

/** Invitation preference is device-local; individual actions travel with the run. */
export function useOnboarding() {
  const run = useRunState()
  const preference = useState<OnboardingPreference>('onboarding-preference', () => {
    try {
      const saved = readFlag(ONBOARDING_KEY)
      if (saved === 'learning' || saved === 'dismissed' || saved === 'complete') return saved
      return readFlag(TUTORIAL_DONE_KEY) === '1' ? 'complete' : 'new'
    } catch { return 'new' }
  })
  const active = computed(() => !!run.value?.tutorial && run.value.mode !== 'tutorial'
    && run.value.tutorialSeen.includes('guide:active') && !seen('finished'))
  const learning = computed(() => run.value?.mode === 'tutorial' || active.value)

  function setPreference(value: OnboardingPreference) {
    preference.value = value
    writeFlag(ONBOARDING_KEY, value) // 🎓 v7.69: localStorage + sessionStorage + memory (utils/flagStore.ts)
  }
  function seen(id: string): boolean { return run.value?.tutorialSeen.includes(`guide:${id}`) ?? false }
  function mark(id: string) {
    if (run.value && !seen(id)) run.value.tutorialSeen.push(`guide:${id}`)
  }
  function stop() {
    mark('finished')
    useTutorial().skipTutorial()
    setPreference('dismissed')
  }
  function finish() {
    mark('finished')
    if (run.value) {
      for (const id of ['draftIntro', 'map', 'encounter', 'banner', 'mission', 'done']) {
        if (!run.value.tutorialSeen.includes(id)) run.value.tutorialSeen.push(id)
      }
      run.value.tutorial = false
    }
    setPreference('complete')
    writeFlag(TUTORIAL_DONE_KEY, '1')
  }
  return { preference, active, learning, seen, mark, setPreference, stop, finish }
}

/** ⚡ v6.62's "this device has seen a Google sign-in" (plugins/firebase.client.ts). */
const SIGNED_IN_KEY = 'elemental-dispatch-signed-in'
/** 🎓 v7.40: how long the title waits for a signed-in account's cloud copy
 * before deciding on what the device alone says. */
export const FIRST_STEPS_CLOUD_TIMEOUT_MS = 8000
let settling: Promise<void> | null = null
let cloudUid: string | null = null

const storage = (): Storage | null => (typeof localStorage === 'undefined' ? null : localStorage)

/**
 * 🎓 v7.40 (per Javi, D3/D4): the title's first-steps state - whether the
 * welcome card is for this player, and which doors a newcomer's calm title
 * shows. The rules live in utils/firstSteps.ts (shared with the run's
 * `firstRun` stamp); this wires them to the live stores.
 *
 * `decide()` settles ONCE per page load, after the ascension and ledger are
 * loaded and - on a device that has seen a Google sign-in - after the
 * account's cloud copy has answered (or FIRST_STEPS_CLOUD_TIMEOUT_MS passed).
 * Nothing that depends on it shows before, so the card can never appear and
 * then vanish, and a door can only appear, never disappear.
 */
export function useFirstSteps() {
  const onboarding = useOnboarding()
  const ascension = useAscension()
  const lifetime = useLifetimeStats()
  const steps = useState<FirstSteps>('first-steps', () => readFirstSteps(storage()))
  const local = useState<LocalPlay>('first-steps-local', () => readLocalPlay(storage()))
  const cloud = useState<CloudPlay | null>(FIRST_STEPS_CLOUD_STATE, () => null)
  const decided = useState('first-steps-decided', () => false)
  /** 🎓 v7.40 (review): new on this device, but the account could not be
   * heard (a timeout or an error) - calm for this page load only, never
   * written down, so the next load asks the account again. */
  const provisional = useState('first-steps-provisional', () => false)

  /** Re-read the device (the title calls it on every visit). */
  function refresh() {
    steps.value = readFirstSteps(storage())
    local.value = readLocalPlay(storage())
  }

  /** New on this device's own evidence (the account may still say otherwise). */
  const newOnDevice = (play: LocalPlay) => !play.tutorialDone && earlierPlay(play).length === 0

  async function settle() {
    ascension.load()
    lifetime.load()
    refresh()
    let heard = true
    if (newOnDevice(local.value) && accountExpected()) {
      heard = await readCloudWithin(FIRST_STEPS_CLOUD_TIMEOUT_MS)
    }
    refresh()
    markNewcomer(heard)
    decided.value = true
  }

  /** A first-timer's device opens calm from now on (Decision 4) - written
   * down only once the account (when there is one) has been heard. */
  function markNewcomer(heard: boolean) {
    if (steps.value.newcomer || local.value.tutorialDone || earlierPlay(local.value, cloud.value).length > 0) {
      provisional.value = false
      return
    }
    if (heard) {
      steps.value = writeFirstSteps(storage(), { newcomer: true })
      provisional.value = false
    } else provisional.value = true
  }

  async function decide(): Promise<void> {
    if (!settling) settling = settle()
    await settling
    refresh()
  }

  function accountExpected(): boolean {
    const user = useAuthUser().value
    if (user && !user.anonymous) return true
    try { return storage()?.getItem(SIGNED_IN_KEY) === '1' } catch { return false }
  }

  /** The signed-in account's own answer, bounded: on a timeout or an error
   * the title decides on what the device says - for this page load only
   * (false = not heard). An answer that lands after the timeout still
   * counts: doors can appear, never vanish. */
  async function readCloudWithin(ms: number): Promise<boolean> {
    let timer: ReturnType<typeof setTimeout> | null = null
    const reading = readCloud()
    try {
      const answer = await Promise.race([
        reading,
        new Promise<'timeout'>((resolve) => { timer = setTimeout(() => resolve('timeout'), ms) }),
      ])
      if (answer === 'timeout') {
        void reading.then((late) => {
          cloud.value = late
          refresh()
          if (provisional.value) markNewcomer(true)
        }).catch(() => { /* still unheard: the next load asks again */ })
        return false
      }
      cloud.value = answer
      return true
    } catch (error) {
      console.warn('First steps: the account could not be read - deciding on this device alone, for now.', error)
      return false
    } finally {
      if (timer) clearTimeout(timer)
    }
  }

  async function readCloud(): Promise<CloudPlay | null> {
    const fb = await firebaseReady()
    await fb.auth.authStateReady()
    const user = fb.auth.currentUser
    if (!user || user.isAnonymous) return null
    const [meta, save] = await Promise.all([
      fb.fs.getDoc(fb.fs.doc(fb.db, 'users', user.uid)),
      fb.fs.getDoc(fb.fs.doc(fb.db, 'users', user.uid, 'saves', 'current')),
    ])
    // 🧨 A document from before the progress epoch is void (useCloudSync, useRunSync).
    const current = (snap: typeof meta) => (snap.exists() && Number(snap.data().epoch ?? 0) >= PROGRESS_EPOCH ? snap.data() : null)
    const m = current(meta)
    const s = current(save)
    // A live first run up there is this player's own first run (signed in
    // mid-run, run sync seeds it) - not earlier play. A tombstone is.
    let cloudRun = !!s
    try { if (s && typeof s.run === 'string' && (JSON.parse(s.run) as { firstRun?: unknown })?.firstRun === true) cloudRun = false } catch { /* unreadable: counts */ }
    const stardust = useStardust()
    await stardust.refresh()
    cloudUid = user.uid
    return {
      ascensionUnlocked: Number(m?.ascensionUnlocked ?? 0) || 0,
      ascensionCleared: Array.isArray(m?.ascensionCleared) ? m.ascensionCleared.length : 0,
      puzzleBests: m?.puzzleBest && typeof m.puzzleBest === 'object' ? Object.keys(m.puzzleBest).length : 0,
      run: cloudRun,
      walletBalance: stardust.balance.value,
    }
  }

  /** A Google sign-in made on the title (after the decision): read that
   * account too, so its doors open and the card stops offering itself. */
  async function recheckAccount(uid: string | null | undefined) {
    // Only a newcomer's calm title can change its mind (a returning player's
    // title never reads the account here).
    if (!uid || uid === cloudUid || !decided.value || !calm.value) return
    if (useAuthUser().value?.anonymous) return
    const heard = await readCloudWithin(FIRST_STEPS_CLOUD_TIMEOUT_MS)
    refresh()
    if (heard && provisional.value) markNewcomer(true)
  }

  /** This device's first-timer, right now (Start Run asks again). */
  const welcome = computed(() => decided.value && welcomeEligible(local.value, cloud.value, onboarding.preference.value))
  /** The account or this device shows play the newcomer record predates. */
  const veteranNow = computed(() => ascension.unlocked.value > 0 || ascension.cleared.value.length > 0 || cloudSigns(cloud.value).length > 0)
  const runEnded = computed(() => local.value.runsEnded > 0)
  /** Calm title (Decision 4, utils/firstSteps.ts titleCalm) - until the
   * first run has ended. Undecided, a device that is new on its own
   * evidence waits calm: doors may appear once the account answers, and
   * never disappear. */
  const calm = computed(() => titleCalm({
    veteran: veteranNow.value,
    runEnded: runEnded.value,
    newcomer: !!steps.value.newcomer,
    provisional: provisional.value,
    decided: decided.value,
    newOnDevice: newOnDevice(local.value),
  }))
  const practiceSettled = computed(() => local.value.tutorialDone
    || onboarding.preference.value === 'dismissed' || onboarding.preference.value === 'complete')
  const doors = computed(() => titleDoors({ calm: calm.value, runEnded: runEnded.value, practiceSettled: practiceSettled.value }))

  /** The Shop's "You have 700 Stardust to spend!" was seen. */
  function markShopNote() {
    if (!steps.value.shopNote) steps.value = writeFirstSteps(storage(), { shopNote: true })
  }

  return { steps, local, cloud, decided, decide, refresh, recheckAccount, welcome, calm, doors, markShopNote }
}
